import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Plus, GraduationCap, Trees, Landmark, Beaker, BadgeCheck, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ORG_TYPES = [
  { key: 'university',   label: 'University',    icon: GraduationCap },
  { key: 'ngo',          label: 'NGO',           icon: Building2 },
  { key: 'park',         label: 'Park',          icon: Trees },
  { key: 'government',   label: 'Government',    icon: Landmark },
  { key: 'research_lab', label: 'Research Lab',  icon: Beaker },
  { key: 'other',        label: 'Other',         icon: Building2 },
];

function iconForType(t) {
  return (ORG_TYPES.find((x) => x.key === t) || ORG_TYPES[5]).icon;
}

export default function Organizations() {
  const nav = useNavigate();
  const [directory, setDirectory] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/organizations')
      .then((r) => { setDirectory(r.data.directory || []); setMine(r.data.mine || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="pb-24 space-y-5" data-testid="organizations-page">
      <div className="flex items-start justify-between pt-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Research Network</p>
          <h1 className="font-serif text-3xl">Organizations</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Universities, NGOs, parks and research labs that turn WildQuest sightings into science.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="btn-create-org"
          className="flex items-center gap-1.5 rounded-full bg-accent text-black px-3 py-1.5 text-xs font-semibold hover:bg-accent/80"
        >
          <Plus className="w-3.5 h-3.5" /> New
        </button>
      </div>

      {mine.length > 0 && (
        <section data-testid="my-orgs">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Your organizations</p>
          <div className="space-y-2">
            {mine.map((o) => (
              <OrgRow key={o.org_id} org={o} onOpen={() => nav(`/organizations/${o.org_id}`)} highlight />
            ))}
          </div>
        </section>
      )}

      <section data-testid="org-directory">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Verified directory</p>
        {loading ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mx-auto" />
          </div>
        ) : directory.length === 0 ? (
          <div className="rounded-2xl border border-white/5 bg-card p-6 text-center text-xs text-muted-foreground" data-testid="org-directory-empty">
            No verified organizations yet. Be the first to register your research group.
          </div>
        ) : (
          <div className="space-y-2">
            {directory.map((o) => (
              <OrgRow key={o.org_id} org={o} onOpen={() => nav(`/organizations/${o.org_id}`)} />
            ))}
          </div>
        )}
      </section>

      {showCreate && (
        <CreateOrgModal
          onClose={() => setShowCreate(false)}
          onCreated={(org) => { setShowCreate(false); load(); nav(`/organizations/${org.org_id}`); toast.success('Organization created — awaiting verification.'); }}
        />
      )}
    </div>
  );
}

function OrgRow({ org, onOpen, highlight }) {
  const Icon = iconForType(org.type);
  return (
    <button
      onClick={onOpen}
      data-testid={`org-row-${org.org_id}`}
      className={`w-full flex items-center gap-3 rounded-2xl p-3 text-left transition border ${
        highlight ? 'bg-emerald-900/20 border-emerald-500/25 hover:border-emerald-400/40' : 'bg-card border-white/5 hover:border-white/15'
      }`}
    >
      <div className="w-11 h-11 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center">
        {org.logo_url ? (
          <img src={org.logo_url} alt="" className="w-full h-full rounded-xl object-cover" />
        ) : (
          <Icon className="w-5 h-5 text-accent" strokeWidth={1.6} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-serif text-base truncate flex items-center gap-1">
          {org.name}
          {org.verified && <BadgeCheck className="w-3.5 h-3.5 text-emerald-400" />}
        </p>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">
          {org.type.replace('_', ' ')}{org.region_hint ? ` · ${org.region_hint}` : ''}
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </button>
  );
}

function CreateOrgModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', type: 'university', description: '',
    website: '', region_hint: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name || form.name.length < 3) { toast.error('Name must be at least 3 characters.'); return; }
    if (!form.description || form.description.length < 10) { toast.error('Description must be at least 10 characters.'); return; }
    setSaving(true);
    try {
      const res = await api.post('/organizations', form);
      onCreated(res.data.organization);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create organization.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="create-org-modal">
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#0A1712] border border-white/10 rounded-t-3xl sm:rounded-3xl p-5 space-y-3"
      >
        <div>
          <p className="text-[10px] uppercase tracking-widest text-accent">Register</p>
          <h2 className="font-serif text-xl">New organization</h2>
        </div>
        <input
          data-testid="org-form-name"
          placeholder="Name (e.g. McGill Biology)"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
        />
        <select
          data-testid="org-form-type"
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
        >
          {ORG_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <textarea
          data-testid="org-form-description"
          placeholder="Brief description of your research focus"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm resize-none"
        />
        <input
          data-testid="org-form-website"
          placeholder="Website (optional)"
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
        />
        <input
          data-testid="org-form-region"
          placeholder="Region hint (optional)"
          value={form.region_hint}
          onChange={(e) => setForm({ ...form, region_hint: e.target.value })}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
        />
        <div className="flex gap-2 pt-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-white/10 text-xs uppercase tracking-widest text-muted-foreground py-2.5 hover:border-white/25"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="org-form-submit"
            className="flex-1 rounded-lg bg-accent text-black font-semibold text-xs uppercase tracking-widest py-2.5 hover:bg-accent/80 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Register'}
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground text-center pt-1">
          Your organization will be verified by WildQuest staff before appearing in the public directory.
        </p>
      </motion.div>
    </div>
  );
}
